import { CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import PrivateRoute from "./components/common/PrivateRoute";
import { AppStateProvider } from "./contexts/AppStateContext";
import { AuthProvider } from "./contexts/AuthContext";
import CenterDetail from "./pages/CenterDetail";
import Checkout from "./pages/Checkout";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import OrderDetail from "./pages/OrderDetail";
import PaymentSuccess from "./pages/PaymentSuccess";
import PhotoGallery from "./pages/PhotoGallery";
import PhotoUpload from "./pages/PhotoUpload";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#892c8b", // Your brand purple
      light: "#a855a7",
      dark: "#6b1b6d",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#f59e0b", // Complementary orange
      light: "#fbbf24",
      dark: "#d97706",
    },
    background: {
      default: "#faf5ff", // Light purple background
      paper: "#ffffff",
    },
    text: {
      primary: "#1e293b",
      secondary: "#64748b",
    },
    grey: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
  },
  spacing: 8, // 8px base spacing unit
  typography: {
    fontFamily:
      '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.25,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.35,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 8, // Slightly smaller for modern look
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          padding: "10px 24px",
          fontSize: "0.875rem",
        },
        contained: {
          boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          borderRadius: 12,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
        },
        elevation1: {
          boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        },
        elevation2: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#892c8b",
          color: "#ffffff",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          borderRadius: 0,
          border: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "2px 0",
          "&:hover": {
            backgroundColor: "#f8fafc",
          },
        },
      },
    },
  },
});

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppStateProvider>
          <Router>
            <Routes>
              <Route path="/" element={<Login />} />
              {/* Public checkout pages - no authentication required */}
              <Route path="/checkout/:token" element={<Checkout />} />
              <Route path="/payment/success/:token" element={<PaymentSuccess />} />
              {/* Centers overview - main dashboard */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              {/* Center detail with children list and folder management */}
              <Route
                path="/center/:centerId"
                element={
                  <PrivateRoute>
                    <CenterDetail />
                  </PrivateRoute>
                }
              />
              {/* Photo folder view - browse photos */}
              <Route
                path="/center/:centerId/child/:childId/folder/:folderId"
                element={
                  <PrivateRoute>
                    <PhotoGallery />
                  </PrivateRoute>
                }
              />
              {/* Photo upload page */}
              <Route
                path="/center/:centerId/child/:childId/folder/:folderId/upload"
                element={
                  <PrivateRoute>
                    <PhotoUpload />
                  </PrivateRoute>
                }
              />
              {/* Order detail page */}
              <Route
                path="/order/:orderId"
                element={
                  <PrivateRoute>
                    <OrderDetail />
                  </PrivateRoute>
                }
              />
              {/* Legacy overview route - redirect to centers */}
              <Route
                path="/overview"
                element={<Navigate to="/centers" replace />}
              />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Router>
        </AppStateProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
